import { Box, SearchIcon } from '@deckee/deck-hand';
import React, { FC, MouseEvent } from 'react';
import styled, { useTheme } from 'styled-components';
import LocationLockup from '../../../../shared/LocationLockup/LocationLockup';
import Shimmer from '../../../../shared/Shimmer/Shimmer';
import { useWeatherLocationFavouriteToggle } from '../../hooks/useWeatherLocationFavouriteToggle';
import FavouriteStar from './FavouriteStar';

const LocationSelectorRow = styled(Box)`
  border-radius: 12px;
  cursor: pointer;

  @media (hover: hover) {
    &:hover {
      background: ${({ theme }) => theme.colors.lightgrey};
    }
  }
`;

export interface LocationSelectorLocation {
  name?: string;
  description?: string;
  latitude?: number;
  longitude?: number;
}

export interface LocationSelectorProps {
  location?: LocationSelectorLocation | null;
  isLoading?: boolean;
  onSelectLocation: () => void;
  /** Tighter horizontal padding for compact forecast header layout. */
  compact?: boolean;
}

const LocationSelector: FC<LocationSelectorProps> = ({
  location,
  isLoading = false,
  onSelectLocation,
  compact = false,
}) => {
  const theme = useTheme();

  const canFavouriteLocation = Boolean(
    location?.name && location.latitude != null && location.longitude != null,
  );

  const { isFavourited, toggleFavourite } = useWeatherLocationFavouriteToggle({
    name: location?.name,
    latitude: location?.latitude,
    longitude: location?.longitude,
    locationDescription: location?.description,
  });

  const handleFavouriteClick = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    toggleFavourite();
  };

  return (
    <LocationSelectorRow
      display="flex"
      alignItems="center"
      gap={2}
      px={compact ? 2 : 3}
      py="10px"
      onClick={onSelectLocation}
      data-testid="weather-header-location"
      data-location-loaded={!isLoading}
      aria-busy={isLoading}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        flex="0 0 auto"
        data-testid="weather-header-search"
      >
        <SearchIcon color={theme.colors.primary} size={24} />
      </Box>
      <Box flex="1 1 auto" minWidth="0" textAlign="center">
        <LocationLockup
          showIcon={false}
          useEllipsis
          name={
            isLoading ? (
              <Shimmer on>
                <Box as="span" minHeight="20px" display="block">
                  {'\u00a0'}
                </Box>
              </Shimmer>
            ) : (
              location?.name || 'Select a location'
            )
          }
          description={
            isLoading ? (
              <Shimmer on forceSpace>
                <Box as="span" minHeight="18px" display="block">
                  {'\u00a0'}
                </Box>
              </Shimmer>
            ) : (
              location?.description
            )
          }
        />
      </Box>
      <FavouriteStar
        filled={isFavourited}
        size={24}
        onClick={canFavouriteLocation ? handleFavouriteClick : undefined}
        data-testid="weather-header-favourite-star"
      />
    </LocationSelectorRow>
  );
};

export default LocationSelector;
